/**
 * TagManageRow Container Component
 *
 * Handles the logic for tag management row including callback wrapping
 * for color editing, name editing, and deletion.
 * @module features/player-kanban/components/TagManageRow
 */

import { memo, useCallback } from "react";

import { type Tag } from "@/features/player-kanban/types/types";

import TagManageRowView from "./TagManageRowView";

/**
 * Props for TagManageRowContainer component
 */
interface TagManageRowContainerProps {
  readonly tag: Tag & { color: string };
  readonly tagUsageCounts: Record<string, number>;
  readonly onStartColorEdit: (tagId: string, currentColor: string) => void;
  readonly onStartEdit: (tagId: string, currentName: string) => void;
  readonly onDeleteTag: (tagId: string) => void;
}

/**
 * Container component for tag manage row
 * Handles logic for tag management actions
 * @param root0 - Component props
 * @param root0.tag - The tag to render
 * @param root0.tagUsageCounts - Record of how many players use each tag
 * @param root0.onStartColorEdit - Callback to start editing tag color
 * @param root0.onStartEdit - Callback to start editing tag name
 * @param root0.onDeleteTag - Callback to delete the tag
 */
const TagManageRowContainer = ({
  tag,
  tagUsageCounts,
  onStartColorEdit,
  onStartEdit,
  onDeleteTag,
}: TagManageRowContainerProps) => {
  const handleColorEdit = useCallback(() => {
    onStartColorEdit(tag.id, tag.color);
  }, [onStartColorEdit, tag.id, tag.color]);

  const handleNameEdit = useCallback(() => {
    onStartEdit(tag.id, tag.name);
  }, [onStartEdit, tag.id, tag.name]);

  const handleDelete = useCallback(() => {
    onDeleteTag(tag.id);
  }, [onDeleteTag, tag.id]);

  const playerCount = tagUsageCounts[tag.id] || 0;

  return (
    <TagManageRowView
      tagName={tag.name}
      tagColor={tag.color}
      playerCount={playerCount}
      onColorEdit={handleColorEdit}
      onNameEdit={handleNameEdit}
      onDelete={handleDelete}
    />
  );
};

TagManageRowContainer.displayName = "TagManageRowContainer";

export default memo(TagManageRowContainer);

export type { TagManageRowContainerProps };
